import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SectionHero from "components/SectionHero/SectionHero";
import SectionSliderNewCategories from "components/SectionSliderNewCategories/SectionSliderNewCategories";
import SectionSubscribe2 from "components/SectionSubscribe2/SectionSubscribe2";
import SectionOurFeatures from "components/SectionOurFeatures/SectionOurFeatures";
import SectionGridFeaturePlaces from "./SectionGridFeaturePlaces";
import SectionHowItWork from "components/SectionHowItWork/SectionHowItWork";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import SectionGridAuthorBox from "components/SectionGridAuthorBox/SectionGridAuthorBox";
import SectionGridCategoryBox from "components/SectionGridCategoryBox/SectionGridCategoryBox";
import SectionBecomeAnAuthor from "components/SectionBecomeAnAuthor/SectionBecomeAnAuthor";
import SectionVideos from "./SectionVideos";
import SectionClientSay from "components/SectionClientSay/SectionClientSay";
import useFetch from "hooks/useFetch";
import config from "config.json";
import cities from 'hotels/Cities/cities.json';
import NcImage from "shared/NcImage/NcImage";
import DocumentationAlert from "components/Alert/DocumentationAlert";

interface Hotel {
  codigo: string;
  descripcion: string;
  ciudad: string;
  thumbnail?: string;
}

interface TaxonomyType {
  id: string;
  name: string;
  taxonomy: "category" | "tag";
  count: number;
  thumbnail: string[];
}

function PageHome() {
  const [categories, setCategories] = useState<TaxonomyType[]>([]);
  const { data, isLoading, error, fetchData } = useFetch('GET', `${config.backend_url}/setia/hoteles`, { 'Content-Type': 'application/json' });
  const { data: dataImagenesCiudades, error: errorImagenesCiudades, fetchData: fetchDataImagenesCiudades } = useFetch(
    'GET',
    `${config.backend_url}/setia/hoteles/lista_fotos?tipo=ciudades`,
    { 'Content-Type': 'application/json' }
  );

  useEffect(() => {
    if (data && dataImagenesCiudades) {
      const response = data as any;
      const hotelsResponse = response.hoteles;

      const responseCiudades = dataImagenesCiudades as any;
      const citiesResponse = responseCiudades.hoteles;

      // Generar categorías a partir de la respuesta de la API
      const categoriesData: TaxonomyType[] = hotelsResponse.map((hotel: Hotel) => {
        const cityData = citiesResponse.find((city: any) => city.hotel_code === hotel.ciudad.toLowerCase().replace(/\s+/g, ''));
        const thumbnail = cityData && cityData.photos ? cityData.photos : [cities.default_image];

        return {
          id: hotel.codigo,
          name: hotel.ciudad,
          taxonomy: "category",
          count: 1,
          thumbnail: thumbnail,
        };
      });

      // Actualizar el estado con las categorías generadas
      setCategories(categoriesData);
      console.log(JSON.stringify(categoriesData));
    }

    if (error) {
      console.log(error);
    }
  }, [data, dataImagenesCiudades]);

  useEffect(() => {
    fetchData();
    fetchDataImagenesCiudades();
    sessionStorage.removeItem('selected_hotel');
  }, []);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -250, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 250, behavior: 'smooth' });
    }
  };

  return (
    <>
    
      <div className="nc-PageHome relative overflow-hidden">
        {/* GLASSMOPHIN */}
        <BgGlassmorphism />

        <div className="container relative space-y-16 mb-16 lg:space-y-20 lg:mb-20">
          {/* SECTION HERO */}
          <SectionHero className="pt-10 lg:pt-16 lg:pb-16" />

          {/* Título y botones de scroll */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-3xl font-semibold text-neutral-900 dark:text-neutral-100">
              Ciudades destacadas
            </h2>
            <div className="hidden sm:flex space-x-4">
              <button
                className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"
                onClick={scrollLeft}
              >
                &#10094;
              </button>
              <button
                className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"
                onClick={scrollRight}
              >
                &#10095;
              </button>
            </div>
          </div>

          {/* Renderizar las categorías usando CardCategory3 */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-scroll space-x-6 pb-4 sm:overflow-x-hidden"
          >
            {categories.map((category) => (
              <CardCategory3 key={category.id} taxonomy={category} />
            ))}
          </div>

          {/* Botones de scroll para mobile */}
          <div className="flex sm:hidden justify-center space-x-4 mt-0">
            <button
              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"
              onClick={scrollLeft}
            >
              &#10094;
            </button>
            <button
              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"
              onClick={scrollRight}
            >
              &#10095;
            </button>
          </div>

          {/* SECTION */}
          <div className="relative py-16">
            <BackgroundSection />
            <SectionGridFeaturePlaces />
          </div>
          <div className="relative py-0">
          </div>
        </div>
      </div>
    </>

  );
}

function CardCategory3({ taxonomy }: { taxonomy: TaxonomyType }) {
  const { name, thumbnail } = taxonomy;
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleCityRedirect = () => {
    window.location.href = `/listing-stay?city=${name}`;
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % thumbnail.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + thumbnail.length) % thumbnail.length);
  };

  const hasImages = Array.isArray(thumbnail) && thumbnail.length > 0;
  const currentImage = hasImages ? thumbnail[currentIndex] : '';

  return (
    <div className="nc-CardCategory3 flex flex-col h-fit w-[250px] flex-shrink-0 snap-start">
      <div
        className="relative w-full aspect-h-4 sm:aspect-h-7 rounded-2xl overflow-hidden group cursor-pointer"
        onClick={handleCityRedirect}
      >
        {hasImages ? (
          <div className="relative w-full h-full">
            <NcImage
              src={currentImage}
              className="object-cover h-[250px] rounded-2xl w-full"
              alt={name}
            />
            {thumbnail.length > 1 && (
              <>
                <button className="carousel-control left" onClick={(e) => { e.stopPropagation(); prevSlide(); }}>
                  &#10094;
                </button>
                <button className="carousel-control right" onClick={(e) => { e.stopPropagation(); nextSlide(); }}>
                  &#10095;
                </button>
                <div className="indicators">
                  {thumbnail.map((_, index) => (
                    <span
                      key={index}
                      className={`indicator ${index === currentIndex ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                    ></span>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="w-full h-full bg-gray-200 rounded-2xl flex items-center justify-center">
            <span className="text-gray-600">Sin imagen</span>
          </div>
        )}
      </div>
      <div className="mt-4 truncate flex justify-center">
        <h2 className="text-base sm:text-lg text-neutral-900 dark:text-neutral-100 font-medium truncate">
          {name}
        </h2>
      </div>
      <style>{`
        .carousel-control {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          padding: 10px;
          cursor: pointer;
          z-index: 10;
          font-size: 18px;
          line-height: 1;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .carousel-control.left {
          left: 10px;
        }

        .carousel-control.right {
          right: 10px;
        }

        .indicators {
          position: absolute;
          bottom: 10px;
          width: 100%;
          text-align: center;
          z-index: 10;
        }

        .indicator {
          display: inline-block;
          width: 10px;
          height: 10px;
          margin: 0 3px;
          background-color: white;
          border-radius: 50%;
          cursor: pointer;
        }

        .indicator.active {
          background-color: black;
        }
      `}</style>
    </div>
  );
}


export default PageHome;
